import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import ProtectedRoute from './components/Layout/ProtectedRoute';
import SessionChecker from "./components/Auth/SessionChecker";
import CookieBanner from './components/UI/CookieBanner';
import GoogleAnalytics from './components/UI/GoogleAnalytics';
import React, { Suspense, useEffect, useState } from "react";
import { UserProvider } from './contexts/UserContext';

const Dashboard = React.lazy(() => import('./pages/Dashboard'));
const Profile = React.lazy(() => import('./pages/Profile'));
const Register = React.lazy(() => import('./pages/Register'));
const Leaderboard = React.lazy(() => import('./pages/Leaderboard'));
const Homepage = React.lazy(() => import('./pages/Homepage'));
const Login = React.lazy(() => import('./pages/Login'));
const ForgotPassword = React.lazy(() => import('./pages/ForgotPassword'));
const Practice = React.lazy(() => import('./pages/Practice'));
const MyJourney = React.lazy(() => import('./pages/MyJourney'));
const Study = React.lazy(() => import('./pages/Study'));
const InvitePage = React.lazy(() => import('./pages/InvitePage'));
const TermsAndConditions = React.lazy(() => import('./pages/TermsAndConditions'));
const PrivacyPolicy = React.lazy(() => import('./pages/PrivacyPolicy'));
const ParentalConsent = React.lazy(() => import('./pages/ParentalConsent'));
const ChangePassword = React.lazy(() => import('./pages/ChangePassword'));
const LessonEngine = React.lazy(() => import('./Core/LessonEngine'));
const Listening = React.lazy(() => import('./components/QuestionTypes/Word/Listening'));

import { getUserProfile } from './utils/api';
import { getCurrentUser } from './utils/auth';

function App() {
  const [userId, setUserId] = useState(null);

  useEffect(() => {
    const currentUser = getCurrentUser();
    if (currentUser) {
      getUserProfile(currentUser.userId).then((profile) => {
        if (profile && profile._id) {
          setUserId(profile._id);
        }
      });
    }
  }, []);

  return (
    <UserProvider>
      <Router>
        {/* ✅ Session Checker works globally */}
        <SessionChecker /> 
        {/* 🍪 Cookie consent banner */}
        <CookieBanner />
        {/* GA4 — loads only after user accepts analytics cookies */}
        <GoogleAnalytics />

        <Suspense fallback={<div />}>
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={
            getCurrentUser() ? <Navigate to="/my-journey" replace /> : <Homepage />
          } />
          <Route path="/register" element={<Register />} />
          <Route path="/login" element={<Login />} />
          <Route path="/listening" element={<Listening />} />

          {/* Protected Routes */}
          <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
          <Route path="/leaderboard" element={<ProtectedRoute><Leaderboard /></ProtectedRoute>} />
          <Route path="/my-journey" element={<ProtectedRoute><MyJourney /></ProtectedRoute>} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/study" element={<ProtectedRoute><Study /></ProtectedRoute>} />
          <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
          <Route path="/lesson" element={<ProtectedRoute><LessonEngine userId={userId} /></ProtectedRoute>} />
          <Route path="/terms" element={<TermsAndConditions />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/parental-consent" element={<ParentalConsent />} />
          <Route path="/change-password" element={<ChangePassword />} />
          <Route path="/invite" element={<ProtectedRoute><InvitePage /></ProtectedRoute>} />
        </Routes>
        </Suspense>
      </Router>
    </UserProvider>
  );
}

export default App;
